import { Flowstep } from '@shared/types/flowstep';
import { EventEmitter } from 'eventemitter3';
import { isObservable, toJS } from 'mobx';
import { v4 as uuid } from 'uuid';

export const FLOWSTEPS_KEY = 'flowsteps';
export const FLOWS_KEY = 'flows';

export interface SaveFlowstepOptions {
  makeCopy?: boolean;
  flowId?: string;
}

const flowTypeMappings: Record<string, string[]> = {
  push: ['insert', 'update', 'delete'],
  pull: ['pull'],
};

export class FlowstepStorage {
  private emitter: EventEmitter = new EventEmitter();

  constructor() {}

  private async saveSettings() {
    return new Promise<void>((resolve, reject) => {
      Office.context.document.settings.saveAsync((asyncResult) => {
        asyncResult.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject();
      });
    });
  }

  private createDefaultFlows() {
    const defaultFlows = {
      pullFlow: {
        id: 'default-pull-flow',
        name: 'Pull Flow',
        options: { flowsteps: [] },
        type: 'pull',
        version: 1,
      },
      pushFlow: {
        id: 'default-push-flow',
        name: 'Push Flow',
        options: { flowsteps: [] },
        type: 'push',
        version: 1,
      },
    };
    Office.context.document.settings.set(FLOWS_KEY, defaultFlows);
    return defaultFlows;
  }

  private isOperationSuitableForFlow(operation: string, flowId: string): boolean {
    const flows = Office.context.document.settings.get(FLOWS_KEY) || {};
    const flow = flows[flowId];

    if (!flow) return false;

    const suitableTypes = flowTypeMappings[flow.type];
    return suitableTypes?.includes(operation) || flow.type === 'custom';
  }

  private assignFlowByType(flowstep: Flowstep): Flowstep {
    let flows = Office.context.document.settings.get(FLOWS_KEY);
    if (!flows) {
      this.createDefaultFlows();
      flows = Office.context.document.settings.get(FLOWS_KEY);
    }

    let flowId = Object.keys(flows).find((flowId) => {
      return this.isOperationSuitableForFlow(flowstep.operation, flowId);
    });

    if (!flowId) {
      flowId = Object.keys(flows).find((flowId) => {
        const flow = flows[flowId];
        return flow.type === 'custom';
      });
    }

    const flow = flowId && flows[flowId];

    if (!flow) {
      //TODO: what if it's no appropriate flow?
      throw new Error(`Can't find suitable flow for flowstep with operation type ${flowstep.operation}`);
    }

    return { ...flowstep, flowId };
  }

  private addFlowstepToFlow(flowstepId: string, flowId: string) {
    const flows = Office.context.document.settings.get(FLOWS_KEY) || {};
    const flow = flows[flowId];
    const alreadyIncluded = flow?.options?.flowsteps?.some((item: any) => item.id === flowstepId);

    if (!alreadyIncluded) {
      flow.options.flowsteps.push({ id: flowstepId });
      Office.context.document.settings.set(FLOWS_KEY, flows);
    }
  }

  private removeFlowstepFromFlow(flowstepId: string, flowId: string) {
    const flows = Office.context.document.settings.get(FLOWS_KEY) || {};
    const flow = flows[flowId];

    if (flow && flow.options && flow.options.flowsteps) {
      const flowstepIndex = flow.options.flowsteps.findIndex((fs: any) => fs.id === flowstepId);
      if (flowstepIndex !== -1) {
        flow.options.flowsteps.splice(flowstepIndex, 1);
        Office.context.document.settings.set(FLOWS_KEY, flows);
      }
    }
  }

  getFlowstep(id: string) {
    const flowsteps = Office.context.document.settings.get(FLOWSTEPS_KEY) || {};
    const flowstep = flowsteps?.[id];
    return structuredClone(isObservable(flowstep) ? toJS(flowstep) : flowstep) as Flowstep;
  }

  private async addFlowstepToSettings(flowstep: Flowstep, makeCopy = false) {
    const flowsteps = Office.context.document.settings.get(FLOWSTEPS_KEY) || {};
    const flowstepId = flowstep?.id && !makeCopy ? flowstep?.id : uuid();

    const updatedFlowstep = {
      ...flowstep,
      id: flowstepId,
    };

    flowsteps[flowstepId] = updatedFlowstep;
    Office.context.document.settings.set(FLOWSTEPS_KEY, flowsteps);

    return updatedFlowstep;
  }

  async saveFlowstep(flowstep: Flowstep, options: SaveFlowstepOptions = {}) {
    const { flowId, makeCopy = false } = options;
    let flowstepClone = { ...flowstep };

    if (flowId && this.isOperationSuitableForFlow(flowstep.operation, flowId)) {
      flowstepClone.flowId = flowId;
    } else if (!flowstepClone.flowId) {
      flowstepClone = this.assignFlowByType(flowstep);
    }

    const savedFlowstep = await this.addFlowstepToSettings(flowstepClone, makeCopy);
    flowstepClone.flowId && this.addFlowstepToFlow(savedFlowstep.id, flowstepClone.flowId);

    await this.saveSettings();
    this.emitter.emit('save-flowstep');

    return savedFlowstep;
  }

  async copyFlowstepToFlow(flowstep: Flowstep, targetFlowId: string) {
    const flowstepWithNewFlow = { ...flowstep, flowId: targetFlowId };
    const savedFlowstep = await this.addFlowstepToSettings(flowstepWithNewFlow, true);

    this.addFlowstepToFlow(savedFlowstep.id, targetFlowId);

    await this.saveSettings();
    this.emitter.emit('save-flowstep');
    return savedFlowstep;
  }

  async moveFlowstepToFlow(flowstep: Flowstep, targetFlowId: string) {
    if (flowstep.flowId) {
      this.removeFlowstepFromFlow(flowstep.id, flowstep.flowId);
    }

    const flowsteps = Office.context.document.settings.get(FLOWSTEPS_KEY) || {};
    const updatedFlowstep = {
      ...flowstep,
      flowId: targetFlowId,
    };

    flowsteps[flowstep.id] = updatedFlowstep;
    Office.context.document.settings.set(FLOWSTEPS_KEY, flowsteps);

    this.addFlowstepToFlow(flowstep.id, targetFlowId);

    await this.saveSettings();
    this.emitter.emit('save-flowstep');

    return updatedFlowstep;
  }

  async deleteFlowstep(flowstep: Flowstep) {
    const flowsteps = Office.context.document.settings.get(FLOWSTEPS_KEY) || {};
    delete flowsteps[flowstep.id];
    Office.context.document.settings.set(FLOWSTEPS_KEY, flowsteps);
    await this.saveSettings();
    this.emitter.emit('delete-flowstep');
  }

  addEventListener(event: string, handler: () => void) {
    this.emitter.on(event, handler);
  }

  findFlowsteps(props: Partial<Flowstep>) {
    const flowstepContainer = Office.context.document.settings.get(FLOWSTEPS_KEY) || {};
    const flowsteps = Object.keys(flowstepContainer).map((flowstepId) => flowstepContainer[flowstepId]);

    function matchesToProps(flowstep: Flowstep, props: Partial<Flowstep>) {
      const compareObjects = (object: Record<string, any>, partial: Record<string, any>): boolean => {
        return Object.keys(partial).every((key) => {
          const value = partial[key];
          if (typeof value === 'object' && typeof object[key] === 'object') {
            return compareObjects(object[key], value);
          } else {
            return partial[key] === object[key];
          }
        });
      };

      return compareObjects(flowstep, props);
    }

    return flowsteps
      .filter((flowstep) => matchesToProps(flowstep, props))
      .map((flowstep) => structuredClone(isObservable(flowstep) ? toJS(flowstep) : flowstep) as Flowstep);
  }
}
